import React, { useState, useEffect, useCallback } from 'react';
import { getAuth } from 'firebase/auth';
import {
  collection, doc, getDoc, getDocs, updateDoc, deleteDoc, deleteField, onSnapshot, setDoc, arrayUnion,
} from 'firebase/firestore';
import { app, db, API_BASE_URL } from '../../firebase/firebaseConfig';
import { ArrowLeft, Plus, Check, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import ErrorModal from '../ui/ErrorModal';
import IglooColumn from './IglooColumn';
import { getIglooColor } from './iglooColors';

function ConfigureHiglos({ onReturn }) {
  const [loading, setLoading] = useState(true);
  const [accountUUID, setAccountUUID] = useState(null);
  const [primaryFamilyID, setPrimaryFamilyID] = useState(null);
  const [families, setFamilies] = useState([]); // [{ familyID, name, autoMode, isPrimary }]
  const [iglooMap, setIglooMap] = useState({}); // { iglooID: { name, positions: { familyID: position } } }
  const [iglooInfoMap, setIglooInfoMap] = useState({}); // { iglooID: { controllers, members } }
  const [externalMembersMap, setExternalMembersMap] = useState({}); // { iglooID: [{ familyID, familyName, isPending }] }
  const [newIglooName, setNewIglooName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Incoming igloo invites for this account's families
  const [incomingInvites, setIncomingInvites] = useState([]);
  // Track which cells are loading (Set of "familyID:iglooID" keys)
  const [loadingCells, setLoadingCells] = useState(new Set()); // [{ familyID, familyName, iglooID, iglooName, fromFamilyName }]

  const showError = useCallback((msg) => {
    setErrorMessage(msg);
    setShowErrorModal(true);
  }, []);

  // Load all data — silent=true skips the loading spinner (for background refreshes)
  const loadData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const auth = getAuth(app);
      const user = auth.currentUser;
      if (!user) return;

      const tokenResult = await user.getIdTokenResult();
      const uuid = tokenResult.claims.accountUUID;
      setAccountUUID(uuid);

      // Get account info
      const accountDoc = await getDoc(doc(db, 'accountsInfo', uuid));
      if (!accountDoc.exists()) return;
      const accountData = accountDoc.data();
      const familyIDs = accountData.families || [];
      const primaryFID = accountData.primaryFamilyID;
      setPrimaryFamilyID(primaryFID);

      // Load family info for each family
      const familyList = [];
      for (const fid of familyIDs) {
        const famDoc = await getDoc(doc(db, 'familyInfo', fid));
        if (famDoc.exists()) {
          const data = famDoc.data();
          familyList.push({
            familyID: fid,
            name: data.name || fid,
            autoMode: data.autoMode || false,
            isPrimary: fid === primaryFID,
          });
        }
      }
      setFamilies(familyList);

      // Load igloos for each family and build a unified igloo map
      const igloos = {}; // iglooID -> { name, positions: { familyID: position } }
      for (const fid of familyIDs) {
        const igloosSnap = await getDocs(collection(db, 'familyInfo', fid, 'igloos'));
        for (const iglooDoc of igloosSnap.docs) {
          const iglooID = iglooDoc.id;
          const data = iglooDoc.data();
          if (!igloos[iglooID]) {
            igloos[iglooID] = { name: data.name || 'Unnamed', positions: {} };
          }
          igloos[iglooID].positions[fid] = data.position || null;
        }
      }
      setIglooMap(igloos);

      // Load iglooInfo for each unique igloo
      const iglooInfos = {};
      const externalMembers = {};
      const ownedFamilySet = new Set(familyIDs);

      for (const iglooID of Object.keys(igloos)) {
        let iglooInfoDoc;
        try {
          iglooInfoDoc = await getDoc(doc(db, 'iglooInfo', iglooID));
        } catch (err) {
          // Orphaned reference — iglooInfo was deleted but familyInfo/igloos still points to it.
          // Security rules deny the read on a non-existent doc. Skip this igloo.
          console.warn(`Skipping igloo ${iglooID}: ${err.code || err.message}`);
          continue;
        }
        if (iglooInfoDoc.exists()) {
          const data = iglooInfoDoc.data();
          iglooInfos[iglooID] = {
            controllers: data.controllers || [],
            members: data.members || [],
          };

          // Find external members (not in this account's families)
          const allMembers = [...(data.controllers || []), ...(data.members || [])];
          const externals = [];
          for (const memberID of allMembers) {
            if (!ownedFamilySet.has(memberID)) {
              // Try connectedFamilies first (any owned family), then fall back to familyInfo
              let memberName = null;
              for (const fid of familyIDs) {
                const connectedDoc = await getDoc(
                  doc(db, 'familyInfo', fid, 'connectedFamilies', memberID)
                );
                if (connectedDoc.exists() && connectedDoc.data().name) {
                  memberName = connectedDoc.data().name;
                  break;
                }
              }
              // Fall back to reading the family's own name from familyInfo
              if (!memberName) {
                const memberFamDoc = await getDoc(doc(db, 'familyInfo', memberID));
                if (memberFamDoc.exists()) {
                  memberName = memberFamDoc.data().name || memberID;
                } else {
                  memberName = memberID;
                }
              }

              // Check if pending
              const pendingDoc = await getDoc(
                doc(db, 'iglooInfo', iglooID, 'pending', memberID)
              );

              externals.push({
                familyID: memberID,
                familyName: memberName,
                isPending: pendingDoc.exists(),
                isController: (data.controllers || []).includes(memberID),
              });
            }
          }
          externalMembers[iglooID] = externals;
        }
      }
      setIglooInfoMap(iglooInfos);
      setExternalMembersMap(externalMembers);

      // Load incoming igloo invites for each family
      const invites = [];
      for (const fid of familyIDs) {
        try {
          const inviteSnap = await getDocs(collection(db, 'iglooInviteMessage', fid, 'iglooIDs'));
          for (const invDoc of inviteSnap.docs) {
            const invIglooID = invDoc.id;
            try {
              // Get the 'from' family from the pending doc
              const pendingDoc = await getDoc(doc(db, 'iglooInfo', invIglooID, 'pending', fid));
              const fromFamilyID = pendingDoc.exists() ? pendingDoc.data().from : null;

              let fromFamilyName = 'Unknown';
              let invIglooName = 'Unknown';
              let fromFamilyIDResolved = fromFamilyID;

              // If we don't have the 'from' from the pending doc, find a controller from iglooInfo
              if (!fromFamilyIDResolved) {
                try {
                  const invIglooInfoDoc = await getDoc(doc(db, 'iglooInfo', invIglooID));
                  if (invIglooInfoDoc.exists()) {
                    const controllers = invIglooInfoDoc.data().controllers || [];
                    if (controllers.length > 0) fromFamilyIDResolved = controllers[0];
                  }
                } catch (e) { /* ignore */ }
              }

              // Get the inviter's family name (familyInfo top-level is readable by all authenticated)
              if (fromFamilyIDResolved) {
                try {
                  const ctrlFamDoc = await getDoc(doc(db, 'familyInfo', fromFamilyIDResolved));
                  if (ctrlFamDoc.exists()) fromFamilyName = ctrlFamDoc.data().name || 'Unknown';
                } catch (e) { /* ignore */ }

                // Try to get igloo name from inviter's igloos subcollection
                // (may fail due to permissions — invitee isn't a controller of inviter's family)
                try {
                  const ctrlIglooDoc = await getDoc(doc(db, 'familyInfo', fromFamilyIDResolved, 'igloos', invIglooID));
                  if (ctrlIglooDoc.exists()) invIglooName = ctrlIglooDoc.data().name || `${fromFamilyName}'s Higlo`;
                } catch (e) {
                  // Permission denied — use "<Family>'s Higlo" as the label
                  invIglooName = `${fromFamilyName}'s Higlo`;
                }
              }

              const famObj = familyList.find(f => f.familyID === fid);
              invites.push({
                familyID: fid,
                familyName: famObj ? famObj.name : fid,
                iglooID: invIglooID,
                iglooName: invIglooName,
                fromFamilyName,
              });
            } catch (inviteErr) {
              console.warn('Error loading invite details for igloo', invIglooID, inviteErr);
              // Still add the invite with limited info so it's visible
              const famObj = familyList.find(f => f.familyID === fid);
              invites.push({
                familyID: fid,
                familyName: famObj ? famObj.name : fid,
                iglooID: invIglooID,
                iglooName: 'Unknown',
                fromFamilyName: 'Unknown',
              });
            }
          }
        } catch (err) {
          console.warn('Error loading invites for family', fid, err);
        }
      }
      setIncomingInvites(invites);
    } catch (error) {
      console.error('Error loading Configure Higlos data:', error);
      showError('Failed to load configuration data.');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Snapshot listeners on iglooInviteMessage for real-time invite updates
  // Use a ref to track family IDs so we only re-subscribe when the actual IDs change
  const familyIDsKey = families.map(f => f.familyID).sort().join(',');
  useEffect(() => {
    if (!familyIDsKey) return;
    const familyIDs = familyIDsKey.split(',');

    // Skip the first snapshot fire (which is the initial data) to avoid double-loading
    const initialFires = new Set(familyIDs);

    const unsubs = [];
    for (const fid of familyIDs) {
      const inviteCollRef = collection(db, 'iglooInviteMessage', fid, 'iglooIDs');
      const unsub = onSnapshot(inviteCollRef, () => {
        if (initialFires.has(fid)) {
          initialFires.delete(fid);
          return; // Skip the initial snapshot
        }
        loadData(true);
      }, (err) => {
        console.warn('Invite snapshot error for', fid, err.code);
      });
      unsubs.push(unsub);
    }

    return () => unsubs.forEach(u => u());
  }, [familyIDsKey, loadData]);

  // Snapshot listeners on iglooInfo/{iglooID}/pending for igloos we control
  // So when an invite is accepted/declined, the sending account sees the update in real-time
  // Snapshot listeners on iglooInfo for ALL igloos we're a member of (not just controlled)
  // Detects: promotions, demotions, removals, pending changes
  const memberIglooIDs = Object.entries(iglooInfoMap)
    .filter(([_, info]) => {
      const ownedIDs = families.map(f => f.familyID);
      const allInIgloo = [...(info.controllers || []), ...(info.members || [])];
      return allInIgloo.some(id => ownedIDs.includes(id));
    })
    .map(([iglooID]) => iglooID)
    .sort()
    .join(',');

  useEffect(() => {
    if (!memberIglooIDs) return;
    const iglooIDs = memberIglooIDs.split(',');

    const initialFires = new Set(iglooIDs.flatMap(id => [`pending-${id}`, `doc-${id}`]));
    const unsubs = [];

    for (const iglooID of iglooIDs) {
      // Listen to pending subcollection (invite accepted/declined)
      const pendingCollRef = collection(db, 'iglooInfo', iglooID, 'pending');
      const unsubPending = onSnapshot(pendingCollRef, () => {
        if (initialFires.has(`pending-${iglooID}`)) {
          initialFires.delete(`pending-${iglooID}`);
          return;
        }
        loadData(true);
      }, (err) => {
        console.warn('Pending snapshot error for', iglooID, err.code);
      });
      unsubs.push(unsubPending);

      // Listen to iglooInfo doc itself (members/controllers change — someone leaves)
      const iglooDocRef = doc(db, 'iglooInfo', iglooID);
      const unsubDoc = onSnapshot(iglooDocRef, () => {
        if (initialFires.has(`doc-${iglooID}`)) {
          initialFires.delete(`doc-${iglooID}`);
          return;
        }
        loadData(true);
      }, (err) => {
        console.warn('IglooInfo snapshot error for', iglooID, err.code);
      });
      unsubs.push(unsubDoc);
    }

    return () => unsubs.forEach(u => u());
  }, [memberIglooIDs, loadData]);

  // Handle position change — only called for families that are already members
  const handlePositionChange = async (familyID, iglooID, newPosition) => {
    const currentPosition = iglooMap[iglooID]?.positions[familyID] || null;

    try {
      if (newPosition === null) {
        // Deactivating — remove position (move to Inactive)
        await updateDoc(doc(db, 'familyInfo', familyID, 'igloos', iglooID), {
          position: deleteField()
        });
        await deleteDoc(doc(db, 'familyInfo', familyID, 'activeIgloos', iglooID)).catch(() => {});

        setIglooMap(prev => {
          const updated = { ...prev };
          if (updated[iglooID]) {
            updated[iglooID] = {
              ...updated[iglooID],
              positions: { ...updated[iglooID].positions, [familyID]: null }
            };
          }
          return updated;
        });
        return;
      }

      // Deactivate any conflicting igloo at the same position BEFORE checking max-3
      const deactivatedIglooIDs = new Set();
      for (const [otherIglooID, otherIgloo] of Object.entries(iglooMap)) {
        if (otherIglooID !== iglooID && otherIgloo.positions[familyID] === newPosition) {
          await updateDoc(doc(db, 'familyInfo', familyID, 'igloos', otherIglooID), {
            position: deleteField()
          });
          await deleteDoc(doc(db, 'familyInfo', familyID, 'activeIgloos', otherIglooID)).catch(() => {});
          deactivatedIglooIDs.add(otherIglooID);

          setIglooMap(prev => {
            const updated = { ...prev };
            if (updated[otherIglooID]) {
              updated[otherIglooID] = {
                ...updated[otherIglooID],
                positions: { ...updated[otherIglooID].positions, [familyID]: null }
              };
            }
            return updated;
          });
        }
      }

      // Check max 3 active igloos (excluding just-deactivated ones)
      const activeCount = Object.entries(iglooMap).filter(
        ([iid, igloo]) => igloo.positions[familyID] && iid !== iglooID && !deactivatedIglooIDs.has(iid)
      ).length;
      if (activeCount >= 3 && !currentPosition) {
        showError('Maximum 3 active Higlos per Hearth.');
        return;
      }

      // Update position
      await updateDoc(doc(db, 'familyInfo', familyID, 'igloos', iglooID), {
        position: newPosition
      });
      await setDoc(doc(db, 'familyInfo', familyID, 'activeIgloos', iglooID), {});

      setIglooMap(prev => {
        const updated = { ...prev };
        if (updated[iglooID]) {
          updated[iglooID] = {
            ...updated[iglooID],
            positions: { ...updated[iglooID].positions, [familyID]: newPosition }
          };
        }
        return updated;
      });

    } catch (error) {
      console.error('Error changing position:', error);
      showError('Failed to update position.');
    }
  };

  // Add an owned family to an igloo as controller (no position yet — Inactive)
  const handleAddToIgloo = async (familyID, iglooID) => {
    const cellKey = `${familyID}:${iglooID}`;
    setLoadingCells(prev => new Set([...prev, cellKey]));
    try {
      const auth = getAuth(app);
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/user/addOwnedFamilyToIgloo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ targetFamilyID: familyID, iglooID }),
      });
      const result = await response.json();
      if (!response.ok) {
        showError(result.error || 'Failed to add family to Higlo.');
        return;
      }
      // Optimistic update — add family as controller, add igloo position entry
      setIglooInfoMap(prev => {
        const info = prev[iglooID] || { controllers: [], members: [] };
        return { ...prev, [iglooID]: { ...info, controllers: [...info.controllers, familyID] } };
      });
      setIglooMap(prev => {
        const igloo = prev[iglooID];
        if (!igloo) return prev;
        return { ...prev, [iglooID]: { ...igloo, positions: { ...igloo.positions, [familyID]: null } } };
      });
    } catch (error) {
      console.error('Error adding family to igloo:', error);
      showError('Failed to add family to Higlo.');
    } finally {
      setLoadingCells(prev => { const next = new Set(prev); next.delete(cellKey); return next; });
    }
  };

  // Check if a family can leave an igloo (not the last member, not the last controller)
  const canLeaveIgloo = (familyID, iglooID) => {
    const info = iglooInfoMap[iglooID];
    if (!info) return false;
    const allMembers = [...(info.controllers || []), ...(info.members || [])];
    // Can't leave if you're the last member overall
    if (allMembers.length <= 1) return false;
    // Can't leave if you're the last controller
    if ((info.controllers || []).includes(familyID) && (info.controllers || []).length <= 1) return false;
    return true;
  };

  // Leave an igloo — removes the family from the igloo
  const handleLeaveIgloo = async (familyID, iglooID) => {
    if (!canLeaveIgloo(familyID, iglooID)) {
      showError("Can't leave: you're the last member or the last controller of this Higlo.");
      return;
    }

    const cellKey = `${familyID}:${iglooID}`;
    setLoadingCells(prev => new Set([...prev, cellKey]));
    try {
      const auth = getAuth(app);
      const idToken = await auth.currentUser.getIdToken();

      const controllers = iglooInfoMap[iglooID]?.controllers || [];
      const ownedFamilyIDs = families.map(f => f.familyID);
      const controllerFamilyID = controllers.find(cid => ownedFamilyIDs.includes(cid));

      const response = await fetch(`${API_BASE_URL}/user/removeMemberFromIgloo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          iglooID,
          iglooFamilyID: controllerFamilyID || familyID,
          memberFamilyID: familyID,
        }),
      });
      const result = await response.json();
      if (!response.ok) {
        showError(result.error || 'Failed to leave Higlo.');
        return;
      }
      // Optimistic update — remove family from controllers/members and positions
      const ownedIDs = families.map(f => f.familyID);
      setIglooInfoMap(prev => {
        const info = prev[iglooID];
        if (!info) return prev;
        const newControllers = (info.controllers || []).filter(id => id !== familyID);
        const newMembers = (info.members || []).filter(id => id !== familyID);
        // If no owned families remain as members, remove the igloo entirely
        const remainingOwned = [...newControllers, ...newMembers].filter(id => ownedIDs.includes(id));
        if (remainingOwned.length === 0) {
          const { [iglooID]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [iglooID]: { controllers: newControllers, members: newMembers } };
      });
      setIglooMap(prev => {
        const igloo = prev[iglooID];
        if (!igloo) return prev;
        const newPositions = { ...igloo.positions };
        delete newPositions[familyID];
        // If no owned families have positions left, check if any are still members
        const anyOwnedInPositions = Object.keys(newPositions).some(fid => ownedIDs.includes(fid));
        if (!anyOwnedInPositions) {
          // Remove the igloo from the map entirely — column disappears
          const { [iglooID]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [iglooID]: { ...igloo, positions: newPositions } };
      });
    } catch (error) {
      console.error('Error leaving igloo:', error);
      showError('Failed to leave Higlo.');
    } finally {
      setLoadingCells(prev => { const next = new Set(prev); next.delete(cellKey); return next; });
    }
  };

  // Create new igloo
  const handleCreateIgloo = async () => {
    const trimmedName = newIglooName.trim();
    if (!trimmedName) return;
    if (!primaryFamilyID) {
      showError('No primary family found.');
      return;
    }

    setIsCreating(true);
    try {
      const auth = getAuth(app);
      const idToken = await auth.currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/user/createIgloo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ familyID: primaryFamilyID, newIglooName: trimmedName }),
      });
      const result = await response.json();
      if (!response.ok) {
        showError(result.error || 'Failed to create Higlo.');
        return;
      }
      setNewIglooName('');
      // Reload all data
      await loadData(true);
    } catch (error) {
      console.error('Error creating igloo:', error);
      showError('Failed to create Higlo.');
    } finally {
      setIsCreating(false);
    }
  };

  // Edit igloo name
  const handleEditIglooName = async (iglooID, newName) => {
    try {
      // Update name in all families that have this igloo
      for (const family of families) {
        if (iglooMap[iglooID]?.positions.hasOwnProperty(family.familyID)) {
          await updateDoc(doc(db, 'familyInfo', family.familyID, 'igloos', iglooID), {
            name: newName
          });
        }
      }
      setIglooMap(prev => ({
        ...prev,
        [iglooID]: { ...prev[iglooID], name: newName }
      }));
    } catch (error) {
      console.error('Error updating igloo name:', error);
      showError('Failed to rename Higlo.');
    }
  };

  // Rename an external family (updates connectedFamilies for all owned families)
  const handleRenameExternal = async (externalFamilyID, newName) => {
    try {
      for (const family of families) {
        await setDoc(
          doc(db, 'familyInfo', family.familyID, 'connectedFamilies', externalFamilyID),
          { name: newName },
          { merge: true }
        );
      }
      // Update local state
      setExternalMembersMap(prev => {
        const updated = { ...prev };
        for (const iid of Object.keys(updated)) {
          updated[iid] = (updated[iid] || []).map(m =>
            m.familyID === externalFamilyID ? { ...m, familyName: newName } : m
          );
        }
        return updated;
      });
    } catch (error) {
      console.error('Error renaming external family:', error);
      showError('Failed to rename family.');
    }
  };

  // Delete igloo
  const handleDeleteIgloo = async (iglooID) => {
    try {
      const auth = getAuth(app);
      const idToken = await auth.currentUser.getIdToken();
      // Find which family controls this igloo
      const controllers = iglooInfoMap[iglooID]?.controllers || [];
      const ownedFamilyIDs = families.map(f => f.familyID);
      const controllerFamilyID = controllers.find(cid => ownedFamilyIDs.includes(cid));

      if (!controllerFamilyID) {
        showError('Cannot delete: no controller access.');
        return;
      }

      const response = await fetch(`${API_BASE_URL}/user/deleteIgloo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ familyID: controllerFamilyID, iglooID }),
      });
      const result = await response.json();
      if (!response.ok) {
        showError(result.error || 'Failed to delete Higlo.');
        return;
      }
      await loadData(true);
    } catch (error) {
      console.error('Error deleting igloo:', error);
      showError('Failed to delete Higlo.');
    }
  };

  // Invite by email — matches the existing IglooMembers.js invite pattern
  const handleInviteByEmail = async (iglooID, email, knownName) => {
    try {
      const auth = getAuth(app);
      const idToken = await auth.currentUser.getIdToken();

      // Look up the family by email
      const lookupResp = await fetch(`${API_BASE_URL}/user/getPrimaryFamilyIDByEmail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ email }),
      });
      const lookupResult = await lookupResp.json();
      if (!lookupResp.ok || !lookupResult.primaryFamilyID) {
        showError('Could not find a Higlo account for that email.');
        return;
      }

      const invitedFamilyID = lookupResult.primaryFamilyID;

      // Check if already a member
      const allMembers = [
        ...(iglooInfoMap[iglooID]?.controllers || []),
        ...(iglooInfoMap[iglooID]?.members || [])
      ];
      if (allMembers.includes(invitedFamilyID)) {
        showError('This family is already a member of this Higlo.');
        return;
      }

      // Check max 5 members
      if (allMembers.length >= 5) {
        showError('Higlo is at maximum capacity (5 members).');
        return;
      }

      // Find the controller family for this igloo
      const controllers = iglooInfoMap[iglooID]?.controllers || [];
      const ownedFamilyIDs = families.map(f => f.familyID);
      const controllerFamilyID = controllers.find(cid => ownedFamilyIDs.includes(cid));

      if (!controllerFamilyID) {
        showError('No controller access for this Higlo.');
        return;
      }

      // Create pending invite doc (matches existing IglooMembers pattern)
      await setDoc(doc(db, 'iglooInfo', iglooID, 'pending', invitedFamilyID), {
        from: controllerFamilyID,
        role: 'member',
      });

      // Add to iglooInfo.members using arrayUnion
      await updateDoc(doc(db, 'iglooInfo', iglooID), {
        members: arrayUnion(invitedFamilyID)
      });

      // Create iglooInviteMessage (empty doc, matching existing pattern)
      await setDoc(doc(db, 'iglooInviteMessage', invitedFamilyID, 'iglooIDs', iglooID), {});

      // Store the known name in connectedFamilies
      await setDoc(
        doc(db, 'familyInfo', controllerFamilyID, 'connectedFamilies', invitedFamilyID),
        { name: knownName },
        { merge: true }
      );

      await loadData(true);
    } catch (error) {
      console.error('Error inviting by email:', error);
      showError('Failed to send invite.');
    }
  };

  // Accept incoming igloo invite
  const handleAcceptInvite = async (familyID, iglooID) => {
    try {
      // Get the 'from' field from the pending doc
      let invitedBy = null;
      try {
        const pendingDoc = await getDoc(doc(db, 'iglooInfo', iglooID, 'pending', familyID));
        invitedBy = pendingDoc.exists() ? pendingDoc.data().from : null;
      } catch (e) { /* may not have permission */ }

      // Get igloo name from the invite we already loaded
      const invite = incomingInvites.find(inv => inv.familyID === familyID && inv.iglooID === iglooID);
      let invIglooName = invite?.iglooName || 'Unnamed';
      if (invIglooName === 'Unnamed' || invIglooName === 'Unknown') {
        // Try the inviter's igloo doc, fall back to "<Family>'s Higlo"
        if (invitedBy) {
          try {
            const inviterIglooDoc = await getDoc(doc(db, 'familyInfo', invitedBy, 'igloos', iglooID));
            if (inviterIglooDoc.exists()) invIglooName = inviterIglooDoc.data().name || invIglooName;
          } catch (e) {
            // Use the from family name
            try {
              const inviterFamDoc = await getDoc(doc(db, 'familyInfo', invitedBy));
              if (inviterFamDoc.exists()) invIglooName = `${inviterFamDoc.data().name}'s Higlo`;
            } catch (e2) { /* ignore */ }
          }
        }
      }

      // Create the igloo entry for this family (Inactive by default)
      await setDoc(doc(db, 'familyInfo', familyID, 'igloos', iglooID), {
        name: invIglooName,
      });

      // Remove the invite message
      await deleteDoc(doc(db, 'iglooInviteMessage', familyID, 'iglooIDs', iglooID));

      // Remove from pending
      try {
        await deleteDoc(doc(db, 'iglooInfo', iglooID, 'pending', familyID));
      } catch (e) { /* ignore */ }

      // Set up connectedFamilies — only write to OUR family's doc (we can't write to the inviter's)
      if (invitedBy) {
        // Get the inviter's family name (familyInfo top-level is readable by all authenticated)
        try {
          const inviterFamDoc = await getDoc(doc(db, 'familyInfo', invitedBy));
          const inviterName = inviterFamDoc.exists() ? inviterFamDoc.data().name : 'Unknown';
          await setDoc(
            doc(db, 'familyInfo', familyID, 'connectedFamilies', invitedBy),
            { name: inviterName },
            { merge: true }
          );
        } catch (e) { /* ignore */ }
        // Note: the inviter's connectedFamilies entry for us was already set up when they sent the invite
      }

      await loadData(true);
    } catch (error) {
      console.error('Error accepting igloo invite:', error);
      showError('Failed to accept invite.');
    }
  };

  // Decline incoming igloo invite
  const handleDeclineInvite = async (familyID, iglooID) => {
    try {
      // Remove the invite message
      await deleteDoc(doc(db, 'iglooInviteMessage', familyID, 'iglooIDs', iglooID));

      // Remove from pending
      await deleteDoc(doc(db, 'iglooInfo', iglooID, 'pending', familyID)).catch(() => {});

      // Remove from iglooInfo.members
      const iglooInfoDoc = await getDoc(doc(db, 'iglooInfo', iglooID));
      if (iglooInfoDoc.exists()) {
        const data = iglooInfoDoc.data();
        const updatedMembers = (data.members || []).filter(id => id !== familyID);
        await updateDoc(doc(db, 'iglooInfo', iglooID), { members: updatedMembers });
      }

      await loadData(true);
    } catch (error) {
      console.error('Error declining igloo invite:', error);
      showError('Failed to decline invite.');
    }
  };

  // Cancel/revoke a pending invite (from the inviting account's perspective)
  const handleCancelInvite = async (iglooID, invitedFamilyID) => {
    try {
      // Remove from iglooInfo.members
      const iglooInfoDoc = await getDoc(doc(db, 'iglooInfo', iglooID));
      if (iglooInfoDoc.exists()) {
        const data = iglooInfoDoc.data();
        const updatedMembers = (data.members || []).filter(id => id !== invitedFamilyID);
        await updateDoc(doc(db, 'iglooInfo', iglooID), { members: updatedMembers });
      }

      // Remove the pending doc
      await deleteDoc(doc(db, 'iglooInfo', iglooID, 'pending', invitedFamilyID)).catch(() => {});

      // Remove the invite message
      await deleteDoc(doc(db, 'iglooInviteMessage', invitedFamilyID, 'iglooIDs', iglooID)).catch(() => {});

      await loadData(true);
    } catch (error) {
      console.error('Error cancelling invite:', error);
      showError('Failed to cancel invite.');
    }
  };

  // Remove an external (non-pending) family from an igloo
  const handleRemoveExternal = async (iglooID, externalFamilyID) => {
    try {
      const auth = getAuth(app);
      const idToken = await auth.currentUser.getIdToken();

      const controllers = iglooInfoMap[iglooID]?.controllers || [];
      const ownedFamilyIDs = families.map(f => f.familyID);
      const controllerFamilyID = controllers.find(cid => ownedFamilyIDs.includes(cid));

      const response = await fetch(`${API_BASE_URL}/user/removeMemberFromIgloo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          iglooID,
          iglooFamilyID: controllerFamilyID,
          memberFamilyID: externalFamilyID,
        }),
      });
      const result = await response.json();
      if (!response.ok) {
        showError(result.error || 'Failed to remove family.');
        return;
      }
      await loadData(true);
    } catch (error) {
      console.error('Error removing external family:', error);
      showError('Failed to remove family.');
    }
  };

  // Toggle an external family between controller and member
  const handleToggleController = async (iglooID, externalFamilyID, makeController) => {
    try {
      const iglooInfoDoc = await getDoc(doc(db, 'iglooInfo', iglooID));
      if (!iglooInfoDoc.exists()) return;
      const data = iglooInfoDoc.data();
      let controllers = [...(data.controllers || [])];
      let members = [...(data.members || [])];

      if (makeController) {
        // Move from members to controllers
        members = members.filter(id => id !== externalFamilyID);
        if (!controllers.includes(externalFamilyID)) controllers.push(externalFamilyID);
      } else {
        // Move from controllers to members
        controllers = controllers.filter(id => id !== externalFamilyID);
        if (!members.includes(externalFamilyID)) members.push(externalFamilyID);
      }

      await updateDoc(doc(db, 'iglooInfo', iglooID), { controllers, members });

      // Optimistic update
      setIglooInfoMap(prev => ({
        ...prev,
        [iglooID]: { controllers, members }
      }));
      // Update external members map
      setExternalMembersMap(prev => {
        const updated = { ...prev };
        if (updated[iglooID]) {
          updated[iglooID] = updated[iglooID].map(m =>
            m.familyID === externalFamilyID ? { ...m, isController: makeController } : m
          );
        }
        return updated;
      });
    } catch (error) {
      console.error('Error toggling controller:', error);
      showError('Failed to update controller status.');
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen w-full flex-col items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const iglooIDs = Object.keys(iglooMap);
  const ownedFamilyIDs = families.map(f => f.familyID);
  // Compute the max external section height across all columns for alignment
  // Non-pending rows: 28px, pending rows: 42px, plus 8px padding (py-1) + 1px separator
  const maxExternalSectionHeight = Math.max(0, ...iglooIDs.map(id => {
    const externals = externalMembersMap[id] || [];
    if (externals.length === 0) return 0;
    const totalRowHeight = externals.reduce((sum, m) => sum + (m.isPending ? 42 : 28), 0);
    return totalRowHeight + 9; // 8px padding + 1px separator
  }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-3">
      <div className="max-w-6xl mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onReturn} className="flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Configure Higlos</h1>
            <p className="text-sm text-slate-500">Manage which Higlos each of your Hearths participates in</p>
          </div>
        </div>

        {/* Matrix */}
        <Card>
          <CardContent className="p-2 sm:p-4">
            {iglooIDs.length === 0 && (
              <p className="text-center text-muted-foreground mb-4">
                No Higlos yet. Create your first one to get started.
              </p>
            )}

            <div className="flex gap-2 overflow-x-auto pb-2">
              {/* Family labels column — sticky so it stays visible when scrolling */}
              {iglooIDs.length > 0 && (
                <div className="flex flex-col min-w-[110px] w-[110px] sm:min-w-[130px] sm:w-[130px] flex-shrink-0 sticky left-0 z-10 bg-white">
                  {/* Header spacer — aligns with igloo column headers */}
                  <div className="px-2 py-2 border-b border-transparent">
                    <div className="text-xs font-semibold text-slate-500">Hearths</div>
                    <div className="text-[10px] text-transparent">spacer</div>
                  </div>
                  {/* Family name rows — height must match IglooColumn FAMILY_ROW_HEIGHT (76px) */}
                  {families.map((family) => (
                    <div key={family.familyID} className="px-1.5 sm:px-2 h-[76px] flex items-center border-b border-slate-50">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-700 truncate" title={family.name}>
                          {family.name}
                        </div>
                        <div className="flex items-center gap-1">
                          {family.isPrimary && (
                            <span className="text-[10px] text-slate-400">Primary</span>
                          )}
                          {family.autoMode && (
                            <Badge variant="outline" className="text-[9px] px-1 py-0 h-3.5">Auto</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Igloo columns */}
              {iglooIDs.map((iglooID, iglooIndex) => {
                const igloo = iglooMap[iglooID];
                const iglooInfo = iglooInfoMap[iglooID] || { controllers: [], members: [] };
                const allMembers = [...iglooInfo.controllers, ...iglooInfo.members];
                const isController = iglooInfo.controllers.some(cid => ownedFamilyIDs.includes(cid));
                const color = getIglooColor(iglooIndex);
                const externalMembers = externalMembersMap[iglooID] || [];
                // An external igloo is one where none of our families is a controller
                const isExternalIgloo = !isController;

                const ownedFamiliesForColumn = families.map(family => ({
                  familyID: family.familyID,
                  familyName: family.name,
                  isPrimary: family.isPrimary,
                  autoMode: family.autoMode,
                  position: igloo.positions[family.familyID] || null,
                  isMember: allMembers.includes(family.familyID),
                }));

                return (
                  <IglooColumn
                    key={iglooID}
                    iglooID={iglooID}
                    iglooName={igloo.name}
                    ownedFamilies={ownedFamiliesForColumn}
                    externalMembers={externalMembers}
                    memberCount={allMembers.length}
                    isController={isController}
                    onPositionChange={handlePositionChange}
                    onAddToIgloo={handleAddToIgloo}
                    onLeaveIgloo={handleLeaveIgloo}
                    canLeaveIgloo={canLeaveIgloo}
                    onInviteByEmail={handleInviteByEmail}
                    onCancelInvite={handleCancelInvite}
                    onRenameExternal={handleRenameExternal}
                    onRemoveExternal={handleRemoveExternal}
                    onToggleController={handleToggleController}
                    onEditName={handleEditIglooName}
                    onDelete={handleDeleteIgloo}
                    loadingCells={loadingCells}
                    color={color}
                    isExternalIgloo={!isController}
                    maxExternalHeight={maxExternalSectionHeight}
                  />
                );
              })}

              {/* Incoming invite columns */}
              {incomingInvites.map((invite) => (
                <div
                  key={`invite-${invite.familyID}-${invite.iglooID}`}
                  className="flex flex-col min-w-[120px] w-[120px] border border-orange-300 rounded-lg bg-orange-50"
                >
                  {/* Header — same height as other columns: name + from line */}
                  <div className="px-2 py-2 border-b border-orange-200">
                    <div className="text-xs font-semibold text-orange-800 truncate" title={invite.iglooName}>
                      {invite.iglooName}
                    </div>
                    <div className="text-[10px] text-orange-600 mt-0.5">
                      from {invite.fromFamilyName}
                    </div>
                  </div>
                  {/* Family rows — Accept/Decline on the invited family's row, dash on others */}
                  {families.map((family) => (
                    <div
                      key={family.familyID}
                      className="border-b border-orange-100 h-[76px] flex items-center justify-center"
                    >
                      {family.familyID === invite.familyID ? (
                        <div className="flex flex-col gap-1 px-1.5 w-full">
                          <Button
                            variant="default"
                            size="sm"
                            className="bg-green-600 text-white hover:bg-green-700 h-6 text-[10px] w-full"
                            onClick={() => handleAcceptInvite(invite.familyID, invite.iglooID)}
                          >
                            <Check className="h-3 w-3 mr-0.5" />
                            Accept
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-red-300 text-red-600 hover:bg-red-50 h-6 text-[10px] w-full bg-transparent"
                            onClick={() => handleDeclineInvite(invite.familyID, invite.iglooID)}
                          >
                            <X className="h-3 w-3 mr-0.5" />
                            Decline
                          </Button>
                        </div>
                      ) : (
                        <span className="text-[10px] text-orange-200">—</span>
                      )}
                    </div>
                  ))}
                </div>
              ))}

              {/* Create new igloo column */}
              <div className="flex flex-col min-w-[140px] w-[140px] border-2 border-dashed border-slate-200 rounded-lg bg-slate-50/50 justify-center items-center p-3">
                <Plus className="h-8 w-8 text-slate-300 mb-2" />
                <p className="text-xs text-slate-400 text-center mb-3">Create New Higlo</p>
                <Input
                  value={newIglooName}
                  onChange={(e) => setNewIglooName(e.target.value)}
                  placeholder="Name"
                  className="text-sm h-8 mb-2 px-2"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleCreateIgloo();
                  }}
                />
                <Button
                  variant="default"
                  size="sm"
                  className="w-full bg-slate-800 text-white hover:bg-slate-700 text-xs"
                  onClick={handleCreateIgloo}
                  disabled={!newIglooName.trim() || isCreating}
                >
                  {isCreating ? 'Creating...' : 'Create'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <ErrorModal
          isOpen={showErrorModal}
          message={errorMessage}
          onClose={() => {
            setShowErrorModal(false);
            setErrorMessage('');
          }}
        />
      </div>
    </div>
  );
}

export default ConfigureHiglos;
